import { Grid } from '@mui/material';
import { VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { Modal } from '../../../../components/Modal';
import { useCaptureEvents } from '../../../../hooks/useCaptureEvents';
import { intercom } from '../../../../services/intercom';
import { ReferralSlide } from '../../components/referralSlide/ReferralSlide';
import { usePermissions } from '../../hooks/usePermissions';
import { useUpgradeDialogVisibility } from '../../hooks/useUpgradeDialogVisibility';
import { useEndUnfinishedRoutes } from '../../routes-list/hooks/useEndUnfinishedRoutes';
import { useSearchUnfinishedRoutes } from '../../routes-list/hooks/useSearchUnfinishedRoutes';
import { EmailNotificationPill } from './notification-pill/EmailNotificationPill';
import { HelpNotificationPill } from './notification-pill/HelpNotificationPill';
import { OpenRoutesNotificationPill } from './notification-pill/OpenRoutesNotificationPill';
import { ReferralNotificationPill } from './notification-pill/ReferralNotificationPill';
import { SubscribeNotificationPill } from './notification-pill/SubscribeNotificationPill';

interface HomeNotificationsProps {
  refreshTrigger: number;
  onOpenPreferences: () => void;
}

export const HomeNotifications = ({ refreshTrigger, onOpenPreferences }: HomeNotificationsProps) => {
  const { capture } = useCaptureEvents();
  const { canReferUsers, canSubscribe, canEditEmail, canOpenHelp } = usePermissions();
  const [notifications, setNotifications] = useState<React.ReactElement[]>([]);
  const { setDialogVisibility } = useUpgradeDialogVisibility();
  const [isOpenOldRoutesMessage, setIsOpenOldRoutesMessage] = useState<boolean>(false);
  const { data: remainingRoutes } = useSearchUnfinishedRoutes();
  const [openReferralDialog, setOpenReferralDialog] = useState(false);

  //Hook to end the unfinished routes
  const { run: endOpenRoutes, isLoading: unfinishedLoading } = useEndUnfinishedRoutes();

  const getNotifications = () => {
    const canShowNotification = {
      open_routes: (remainingRoutes ?? 0) > 1,
      subscribe: canSubscribe,
      missing_email: canEditEmail,
      help: canOpenHelp,
      referral: canReferUsers,
    };

    const filteredNotifications: React.ReactElement[] = [];

    if (canShowNotification.referral) {
      filteredNotifications.push(
        <ReferralNotificationPill
          key={`referral_notification`}
          onClick={() => {
            setOpenReferralDialog(true);
          }}
        />
      );
    }

    if (canShowNotification.open_routes) {
      filteredNotifications.push(
        <OpenRoutesNotificationPill
          key={`open_routes_notification`}
          onClick={() => {
            setIsOpenOldRoutesMessage(true);
          }}
        />
      );
    }

    if (canShowNotification.subscribe) {
      filteredNotifications.push(
        <SubscribeNotificationPill
          key={`subscribe_notification`}
          onClick={() => {
            setDialogVisibility(true);
          }}
        />
      );
    }

    if (canShowNotification.missing_email) {
      filteredNotifications.push(<EmailNotificationPill key={`email_notification`} onClick={onOpenPreferences} />);
    }

    if (canShowNotification.help) {
      filteredNotifications.push(<HelpNotificationPill key={`help_notification`} onClick={() => intercom.open()} />);
    }

    setNotifications(filteredNotifications);
  };

  useEffect(() => {
    getNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger, canSubscribe, canEditEmail, canOpenHelp, remainingRoutes]);

  return (
    <>
      <Grid
        container
        direction="column"
        wrap="nowrap"
        gap="10px"
        style={{
          padding: `0 24px`,
        }}
      >
        {notifications}
      </Grid>

      <ReferralSlide
        openSlide={openReferralDialog}
        onClose={() => {
          setOpenReferralDialog(false);
        }}
      />

      <Modal
        open={isOpenOldRoutesMessage}
        isLoading={unfinishedLoading}
        onClose={() => {
          capture(`Close Open Routes Dialog`, {
            remainingRoutes,
            action: `cancel`,
          });
          setIsOpenOldRoutesMessage(false);
        }}
        title={{
          intlId: `driver.route.end_old_routes_message.title`,
        }}
        subtitle={{
          intlId: `driver.route.end_old_routes_message.message`,
          intlValues: { remainingRoutes: remainingRoutes ?? 0 },
        }}
        buttons={[
          {
            intlId: `driver.route.end_old_routes_message.button`,
            onClick: () => {
              capture(`Close Open Routes Dialog`, {
                remainingRoutes,
                action: `closeOpenRoutes`,
              });
              endOpenRoutes();
            },
            isLoading: unfinishedLoading,
          },
          {
            intlId: `cancel`,
            onClick: () => {
              capture(`Close Open Routes Dialog`, {
                remainingRoutes,
                action: `cancel`,
              });
              setIsOpenOldRoutesMessage(false);
            },
            isLoading: false,
            variant: `text`,
            style: {
              border: `none`,
            },
          },
        ]}
        children={<VSpacer medium />}
      />
    </>
  );
};
